import type { LearnosityInitializationStatus } from '../../custom-hooks/useLearnosityItemsInitialization';
import { Dispatch, FC, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { getItemReferences } from '../../bffApiCalls/getItemReferences';
import { LearnosityRequest } from '../../models/LearnosityRequest';
import { useLearnosityBffInitialization } from '../../custom-hooks/useLearnosityBffInitialization';
import { useAppSelector } from '../../redux/hooks';
import { selectUser } from '../../redux/reducers/userSlice';
import { v4 as uuid } from 'uuid';
import { useAuth0 } from '@auth0/auth0-react';
import { useComponentTracking } from '../../hooks/useComponentTracking';
import { SkillResourceSteps } from '../../models/Timeline';
import { useLazyLoadQuery, useMutation } from 'react-relay';
import { PracticeProblemsSessionQuery } from '../../graphQLOperations/__generated__/PracticeProblemsSessionQuery.graphql';
import { getPracticeProblemsSession, upsertPracticeProblemsSession } from '../../graphQLOperations/PracticeProblems';
import { PracticeProblemsSessionMutation } from '../../graphQLOperations/__generated__/PracticeProblemsSessionMutation.graphql';
import { clearProgress } from '../../businessLogicService/practiceProblemsHelper';
import { usePracticeProblemUserActionSave } from '../../custom-hooks/usePracticeProblemUserActionSave';
import { usePreviewMode } from '../../contexts/PreviewModeContext';
import { AssessmentState } from '../../custom-hooks/useLrnSession';
import { usePostHog } from '@posthog/react';
import { isAbortError } from '../../utils/fetchCancellation';
import { ThreeDotLoader } from '../../components/Fallback/ThreeDotsLoader';
import { PracticeProblemsAssessmentWrapper } from './PracticeProblemsAssessmentWrapper';
import { practiceProblemsExperimentRegions } from './PracticeProblemsExperimentRegions';
import { useBrainBreakNavigation } from '../../custom-hooks/useBrainBreakNavigation';
import { ErrorBoundaryUI } from '../../components/ErrorBoundary/ErrorBoundaryUI';

interface Props {
  skillCode: string;
  skillId?: string;
  setAssessmentInProgress?: Dispatch<SetStateAction<boolean>>;
  setPrintDisabled?: (state: boolean) => void;
  refetchLrnSession?: () => void;
  setCurrentStep?: Dispatch<SetStateAction<SkillResourceSteps | null>>;
  setSkipNewActivityId?: Dispatch<SetStateAction<boolean>>;
  skipSessionQuery?: boolean;
  learnosityPPKey?: number | null;
  /** Shape of `useFeatureFlagVariantKey`'s return value; forwarded so the variant is read in one place. */
  practiceProblemsExperimentVariant?: string | boolean;
}

export const PracticeProblemsExperimentAssess: FC<Props> = ({
  skillCode,
  skillId,
  setPrintDisabled,
  refetchLrnSession,
  setCurrentStep,
  setSkipNewActivityId,
  skipSessionQuery = false,
  learnosityPPKey,
  setAssessmentInProgress,
  practiceProblemsExperimentVariant,
}) => {
  const previewMode = usePreviewMode();
  const userRedux = useAppSelector(selectUser);
  const { logStarted, logResumed, logReset } = usePracticeProblemUserActionSave();
  const [loading, setLoading] = useState(true);
  const [itemReferences, setItemReferences] = useState<string[] | null>(null);
  const [initObj, setInitObj] = useState<LearnosityRequest | null>(null);
  const [failedStartup, setFailedStartup] = useState<string | null>(null);
  const [showRetryButton, setShowRetryButton] = useState<boolean>(false);
  const handleStatusChange = useCallback((status: LearnosityInitializationStatus) => {
    setLoading(status === 'initializing' || status === 'retry_wait');
  }, []);
  const { getAccessTokenSilently, logout } = useAuth0();
  const posthog = usePostHog();
  const { fetchInitialization, refetchInitialization } = useLearnosityBffInitialization(
    getAccessTokenSilently,
    logout,
    posthog,
  );
  const initController = useRef<AbortController | null>(null);
  const currentInitRef = useRef<{ sessionId: string; state: AssessmentState } | null>(null);
  const initRetriedRef = useRef(false);

  const [commitPracticeProblemSessionId] = useMutation<PracticeProblemsSessionMutation>(upsertPracticeProblemsSession);
  const { learnosityPracticeProblemsSession } = useLazyLoadQuery<PracticeProblemsSessionQuery>(
    getPracticeProblemsSession,
    // A preview has no student session to resume, so previewMode always skips the query.
    { skillId: skillId!, studentId: `StudentNode:${userRedux?.id}`, skip: skipSessionQuery || previewMode },
    { fetchPolicy: 'network-only', fetchKey: `${skillId}-${learnosityPPKey}` },
  );
  const startupKey = `${userRedux?.sub}|${skillCode}|${
    learnosityPracticeProblemsSession?.sessionId ?? ''
  }|${previewMode}`;

  // Track component lifecycle for analytics
  useComponentTracking('Practice Problems');

  const triggerMutationToSaveSessionId = (sessionId: string, action: 'started' | 'reset') => {
    commitPracticeProblemSessionId({
      variables: {
        input: {
          input: {
            sessionId: sessionId,
            studentId: `StudentNode:${userRedux?.id ?? ''}`,
            sessionStartedOn: new Date(),
            skillId: skillId ?? '',
            activityId: `Skill_${skillCode}_Practice_Set`,
            isActive: true,
          },
        },
      },
      onCompleted: (_response, error) => {
        if (error && error.length > 0) {
          console.error('Error saving practice problems session ID:', error[0].message);
        }
        if (action === 'started') {
          setTimeout(() => {
            logStarted(skillId ?? '', sessionId);
          }, 3000);
        } else if (action === 'reset') {
          setTimeout(() => {
            logReset(skillId ?? '', sessionId);
          }, 3000);
        }
      },
    });
  };

  const triggerLearnosityInitObject = (sessionId: string, state: AssessmentState) => {
    currentInitRef.current = { sessionId, state };
    initController.current?.abort();
    const controller = new AbortController();
    initController.current = controller;
    setInitObj(null);
    setFailedStartup(null);
    setLoading(true);
    setPrintDisabled?.(true);

    fetchInitialization(
      {
        state: state,
        activityId: `Skill_${skillCode}_Practice_Set`,
        userId: userRedux?.sub || '',
        sessionId: sessionId,
        renderingType: 'assess',
        regions: practiceProblemsExperimentRegions,
        title: 'Practice Problems',
        showDistractorRationale: true,
        responsiveRegions: true,
        warningOnChange: false,
        scrollToTest: false,
        showIntro: false,
        scrollToTop: false,
      },
      { signal: controller.signal },
    )
      .then(initObject => {
        if (controller.signal.aborted) return;
        setInitObj(initObject);
      })
      .catch(err => {
        if (controller.signal.aborted || isAbortError(err)) return;
        setFailedStartup(startupKey);
        console.log('Error fetching practice problems init object', err);
        setLoading(false);
      });
  };

  const handleResetButton = () => {
    const newSessionId = uuid();

    clearProgress(userRedux?.id ?? '', skillCode);
    triggerLearnosityInitObject(newSessionId, 'initial');
    if (!previewMode) {
      triggerMutationToSaveSessionId(newSessionId, 'reset');
    }
    setShowRetryButton(false);
  };

  const handleInitError = () => {
    if (initRetriedRef.current || !currentInitRef.current) return;
    initRetriedRef.current = true;
    const { sessionId, state } = currentInitRef.current;
    triggerLearnosityInitObject(sessionId, state);
  };

  const handleTakeChallenge = useBrainBreakNavigation(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setSkipNewActivityId?.(false);
    setAssessmentInProgress?.(true);
    setCurrentStep?.(SkillResourceSteps.SKILL_CHALLENGE);
    refetchLrnSession?.();
  }, 'practice-problems');

  useEffect(() => {
    if (itemReferences) {
      setLoading(false);
    }
  }, [itemReferences]);

  useEffect(() => {
    const controller = new AbortController();
    setItemReferences(null);
    if (skillCode && userRedux?.sub) {
      getItemReferences(`Skill_${skillCode}_Practice_Set`, getAccessTokenSilently, logout, posthog, {
        signal: controller.signal,
        terminalStartup: false,
      })
        .then(itemRef => {
          if (controller.signal.aborted) return;
          setItemReferences(itemRef);
        })
        .catch(err => {
          if (controller.signal.aborted || isAbortError(err)) return;
          console.log('Error fetching item references', err);
          setLoading(false);
        });
    }
    return () => controller.abort();
  }, [skillCode, userRedux?.sub]);

  useEffect(() => {
    setInitObj(null);
    let sessionId: string;
    if (learnosityPracticeProblemsSession?.sessionId) {
      sessionId = learnosityPracticeProblemsSession.sessionId;
      setShowRetryButton(true);
      logResumed(skillId ?? '', sessionId);
    } else {
      sessionId = uuid();
      if (skillId && !previewMode) triggerMutationToSaveSessionId(sessionId, 'started');
    }

    if (skillCode && userRedux?.sub) {
      triggerLearnosityInitObject(sessionId, learnosityPracticeProblemsSession?.sessionId ? 'resume' : 'initial');
    }
    return () => {
      initController.current?.abort();
    };
  }, [learnosityPracticeProblemsSession?.sessionId, skillCode, userRedux?.sub, previewMode]);

  return (
    <>
      <div
        className="tw-w-full tw-flex tw-flex-col tw-justify-start tw-items-start !tw-relative !tw-z-[2]"
        id="practiceProblems"
      >
        <div className="tw-w-full tw-flex tw-flex-row tw-justify-between "></div>
        <h3 className="tw-font-normal tw-p-1"></h3>
        {failedStartup === startupKey ? (
          <ErrorBoundaryUI embedded />
        ) : initObj ? (
          <PracticeProblemsAssessmentWrapper
            onInitError={handleInitError}
            setPrintDisabled={setPrintDisabled}
            initObj={initObj}
            setShowRetryButton={setShowRetryButton}
            skillCode={skillCode}
            handleTakeChallenge={handleTakeChallenge}
            handleReset={handleResetButton}
            showRetryButton={showRetryButton}
            skillId={skillId}
            onStatusChange={handleStatusChange}
            refetchInitialization={refetchInitialization}
            practiceProblemsExperimentVariant={practiceProblemsExperimentVariant}
          />
        ) : loading ? (
          <ThreeDotLoader dark />
        ) : (
          <h3 className="tw-self-center tw-p-1">Practice problems for this skill coming soon!</h3>
        )}
      </div>
    </>
  );
};
